export interface VideoAdjustments { brightness: number; contrast: number; hue: number; saturation: number }
export const neutralVideoAdjustments: VideoAdjustments = { brightness:0, contrast:0, hue:0, saturation:0 };
export function readVideoAdjustments(value: unknown): VideoAdjustments | undefined {
  if (!value || typeof value !== 'object') return;
  const source = value as Record<string, unknown>;
  const result = {...neutralVideoAdjustments};
  for (const key of Object.keys(result) as (keyof VideoAdjustments)[]) {
    const n = source[key], limit = key === 'hue' ? 180 : 100;
    if (typeof n !== 'number' || !Number.isFinite(n) || n < -limit || n > limit) return;
    result[key] = n;
  }
  return result;
}
export function videoDisplayFilter(value: VideoAdjustments) {
  if (Object.values(value).every(n => n === 0)) return 'none';
  return `brightness(${1+value.brightness/100}) contrast(${1+value.contrast/100}) hue-rotate(${value.hue}deg) saturate(${1+value.saturation/100})`;
}
export function paintVideoBackground(ctx: CanvasRenderingContext2D, image: CanvasImageSource, width: number, height: number, value?: VideoAdjustments) {
  const filter = value ? videoDisplayFilter(value) : 'none';
  const nativeFilter = typeof ctx.filter === 'string';
  ctx.save();
  if (nativeFilter) ctx.filter = filter;
  ctx.drawImage(image, 0, 0);
  ctx.restore();
  if (!nativeFilter && value && filter !== 'none') {
    const pixels = ctx.getImageData(0, 0, width, height);
    adjustVideoPixels(pixels.data, value); ctx.putImageData(pixels, 0, 0);
  }
}
export function reviewVideoAdjustments(comments: {videoAdjustments?: VideoAdjustments; videoAdjustmentsSavedAt?: number}[]) {
  const latest = comments.filter(item => item.videoAdjustments).reduce<typeof comments[number] | undefined>((a,b) => !a || (b.videoAdjustmentsSavedAt || 0) > (a.videoAdjustmentsSavedAt || 0) ? b : a, undefined);
  return readVideoAdjustments(latest?.videoAdjustments) || {...neutralVideoAdjustments};
}

/** Canvas filter is not available in every browser. The fallback uses the
 * sRGB matrices from https://www.w3.org/TR/filter-effects-1/#feColorMatrixElement.
 * Only captured review images are adjusted; the original video stays intact. */
export function adjustVideoPixels(data: Uint8ClampedArray, value: VideoAdjustments) {
  const bright=1+value.brightness/100, contrast=1+value.contrast/100, offset=127.5*(1-contrast);
  const angle=value.hue*Math.PI/180, c=Math.cos(angle), s=Math.sin(angle), saturation=1+value.saturation/100;
  const hue=[.213+.787*c-.213*s, .715-.715*c-.715*s, .072-.072*c+.928*s,
    .213-.213*c+.143*s, .715+.285*c+.140*s, .072-.072*c-.283*s,
    .213-.213*c-.787*s, .715-.715*c+.715*s, .072+.928*c+.072*s];
  const clamp=(n:number)=>Math.max(0,Math.min(255,n));
  for(let i=0;i<data.length;i+=4) {
    const r=clamp(clamp(data[i]*bright)*contrast+offset), g=clamp(clamp(data[i+1]*bright)*contrast+offset), b=clamp(clamp(data[i+2]*bright)*contrast+offset);
    const hr=clamp(hue[0]*r+hue[1]*g+hue[2]*b), hg=clamp(hue[3]*r+hue[4]*g+hue[5]*b), hb=clamp(hue[6]*r+hue[7]*g+hue[8]*b);
    const gray=.213*hr+.715*hg+.072*hb;
    data[i]=gray+(hr-gray)*saturation; data[i+1]=gray+(hg-gray)*saturation; data[i+2]=gray+(hb-gray)*saturation;
  }
}
